import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Context } from '../../..';
import { Tooltip } from 'react-tooltip';
import styles from '../CertificationPage.module.scss';

const CertificationApply: React.FC = () => {
  const { store } = useContext(Context);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const certification = store.certifications.find(cert => cert.id.toString() === id);

  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isLegalEntity, setIsLegalEntity] = useState(false);

  useEffect(() => {
    if (store.certifications.length === 0) {
      store.getCertifications();
    }
  }, [store]);

  const { register, handleSubmit, formState: { errors } } = useForm();
  const [files, setFiles] = useState<{ [key: string]: File | File[] | null }>({
    application: null,
    application_process: null,
    consent: null,
    feedback: null,
    auditor_report: null,
    complaints: null,
    personal_card: null,
    non_disclosure: null,
    additional_files: null,
  });

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>, key: string) => {
    if (key === 'additional_files' && event.target.files) {
      setFiles({ ...files, [key]: Array.from(event.target.files) });
    } else {
      setFiles({ ...files, [key]: event.target.files ? event.target.files[0] : null });
    }
  };

  const onSubmit = async (data: any) => {
    if (!certification || !certification.id) {
      alert('Сертифікація не визначена.');
      return;
    }
    const formData = new FormData();
    formData.append('full_name', data.full_name); // Додати ПІБ до formData

    Object.keys(files).forEach(key => {
      if (key === 'additional_files' && Array.isArray(files[key])) {
        (files[key] as File[]).forEach(file => {
          formData.append(key, file);
        });
      } else if (files[key]) {
        formData.append(key, files[key] as Blob, (files[key] as File)?.name);
      }
    });

    try {
      const response = await store.applyForCertification(certification.id, formData);
      if (response.status === 200) {
        setSubmissionSuccess(true);
        setTimeout(() => {
          navigate(`/certifications/user`);
        }, 2000); // Затримка перед редиректом для демонстрації анімації
      } else {
        alert(response);
      }
    } catch (error) {
      // console.error('Error during submission:', error);
    }
  };

  const downloadTemplate = (filename: string) => {
    const url = `${process.env.REACT_APP_BASE_URL}/forms/${filename}.docx`;
    window.open(url, '_blank');
  };

  if (!certification) {
    return <div className="text-center font-medium text-lg">Інформація про сертифікацію недоступна.</div>;
  }

  return (
    <form className={styles['certification-form']} onSubmit={handleSubmit(onSubmit)}>
      <h1 className={styles['certification-form__title']}>Подати заявку на сертифікацію: {certification.ISO} - {certification.name}</h1>
      
      <div className={styles['certification-form__switch']}>
        <label>
          <input
            type="checkbox"
            checked={isLegalEntity}
            onChange={() => setIsLegalEntity(!isLegalEntity)}
          />
          Я юридична особа
        </label>
      </div>

      {/* Додаємо новий блок для введення ПІБ */}
      <div className={styles['certification-form__file-block']}>
        <div className={styles['certification-form__file-group']}>
          <label className={styles['certification-form__label']} htmlFor="full_name">ПІБ (хто подається):</label>
          <input className={styles['certification-form__input']} id="full_name" {...register('full_name', { required: 'Це поле обов`язкове для заповнення.' })} />
          {errors.full_name && <p className={styles['certification-form__error']}>{errors.full_name.message}</p>}
        </div>
      </div>
      
      <div className={styles['certification-form__file-block']}>
        {[
          { key: "application", label: isLegalEntity ? "Заява на проходження сертифікації (для юридичної особи)" : "Заява на проходження сертифікації" },
          { key: "application_process", label: "Заява на проходження процесу сертифікації" },
          { key: "consent", label: "Згода на обробку персональних даних" },
          { key: "feedback", label: "Відгук-Оцінка про досвід виконання робіт з оцінки відповідності кандидата" },
          { key: "auditor_report", label: "Звіт аудитора про діяльність" },
          { key: "complaints", label: "Довідка про відсутність скарг" },
          { key: "personal_card", label: "Особова картка кандидата" },
        ].map((doc, index) => (
          <div key={index} className={styles['certification-form__file-group']}>
            <label className={styles['certification-form__label']} htmlFor={doc.key}>{doc.label}</label>
            <button type="button" onClick={() => downloadTemplate(doc.key)} className={styles['certification-form__download-button']}>
              Скачати форму
            </button>
            <input className={styles['certification-form__input']} id={doc.key} type="file" accept="application/pdf" {...register(`file${index}`, { required: true })} onChange={(e) => onFileChange(e, doc.key)} />
            {errors[`file${index}`] && <p className={styles['certification-form__error']}>Це поле обов'язкове для заповнення.</p>}
          </div>
        ))}
        <div className={styles['certification-form__file-group']}>
          <div className={styles["tooltip-container"]}>
            <label className={styles['certification-form__label']} htmlFor="additional_files">Додаткові файли:</label>
            <span data-tooltip-id='additionalFilesTooltip' className={styles["tooltip-icon"]}>?</span>
            <Tooltip id='additionalFilesTooltip' place='top'>
              <div>
                До заяви додаються документи, які підтверджують:
                <ul>
                  <li>вищу освіту бажано у відповідній сфері;</li>
                  <li>проходження навчання та підготовки за відповідним напрямом в учбовій організації, що проводить таку підготовку;</li>
                  <li>загальний стаж роботи;</li>
                  <li>досвід роботи за напрямом.</li>
                </ul>
              </div>
            </Tooltip>
          </div>
          <input className={styles['certification-form__input']} id="additional_files" type="file" accept="application/pdf" multiple {...register('additionalFiles', {required: true})} onChange={(e) => onFileChange(e, "additional_files")} />
          {errors[`additionalFiles`] && <p className={styles['certification-form__error']}>Це поле обов'язкове для заповнення.</p>}
        </div>
      </div>

      <div className={styles["certification-form__button-group"]}>
        <button type="submit" className={styles['certification-form__button']}>
          Подати заявку
        </button>
        <Link to="/certifications" className={styles['certification-form__cancel-link']}>Скасувати</Link>
      </div>
      {submissionSuccess && <div className={styles["success-message"]}>Заявка успішно подана!</div>}
    </form>
  );
};

export default observer(CertificationApply);
